import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PROSPECTS_KEY,
  checkAccountEmail,
  createProspectAccount
} from '../services/prospectService';
import { EMAIL_REGEX, validateStrict } from '../utils/creationRequestValidation';

/**
 * Étape 2 — le geste « Enregistrer la demande » depuis la fiche prospect (§2.3 et §2.4).
 *
 * Deux responsabilités, volontairement séparées :
 *  1. `useVerificationEmail` (A1 / A4) — l'adresse est-elle déjà prise ? Debounce de 400 ms, et
 *     les réponses **obsolètes sont ignorées** : sans le compteur `tentative`, une frappe rapide
 *     ferait clignoter « compte existant » pour l'adresse précédente.
 *  2. `useCreateProspectAccount` — la mutation. Elle applique la **garde stricte en premier** :
 *     inutile d'aller au serveur pour un champ de 3 caractères, et le message d'erreur est déjà
 *     rangé par chemin pointé (`businessInfo.adresse.rue`), la même forme que `details.fields`.
 *
 * ⚠️ Ni l'une ni l'autre ne remplace le serveur : la callable revérifie les 7 gardes. L'UI accélère,
 * elle ne protège pas.
 */

/** Délai du debounce (ms) — §2.3 : vérification « onBlur + debounce 400 ms ». */
export const DELAI_VERIFICATION = 400;

export const ETAT_EMAIL_INITIAL = {
  email: '',
  enCours: false,
  existe: false,
  verifie: false,
  erreur: null,
  details: null
};

/**
 * Vérifie qu'une adresse ne possède pas déjà un compte (A1).
 *
 * @param {{delai?: number}} [options] délai de debounce en millisecondes
 * @returns {object} l'état courant (`email`, `enCours`, `existe`, `verifie`, `erreur`, `details`)
 *   et `verifier(email)`, à appeler à chaque frappe.
 */
export const useVerificationEmail = ({ delai = DELAI_VERIFICATION } = {}) => {
  const [etat, setEtat] = useState(ETAT_EMAIL_INITIAL);
  const minuteur = useRef(null);
  const tentative = useRef(0);

  const annuler = useCallback(() => {
    if (minuteur.current) {
      clearTimeout(minuteur.current);
      minuteur.current = null;
    }
  }, []);

  const verifier = useCallback(
    (valeur) => {
      annuler();
      // Toute nouvelle frappe invalide la réponse en vol : elle concernait l'adresse précédente.
      tentative.current += 1;
      const numero = tentative.current;
      const email = String(valeur || '').trim().toLowerCase();

      if (!EMAIL_REGEX.test(email)) {
        setEtat({ ...ETAT_EMAIL_INITIAL, email });
        return;
      }

      setEtat({ ...ETAT_EMAIL_INITIAL, email, enCours: true });

      minuteur.current = setTimeout(async () => {
        try {
          const details = await checkAccountEmail(email);
          if (tentative.current !== numero) return;
          setEtat({
            email,
            enCours: false,
            existe: details?.exists === true,
            verifie: true,
            erreur: null,
            details
          });
        } catch (erreur) {
          if (tentative.current !== numero) return;
          // Un échec réseau n'est PAS « pas de compte » : `verifie` reste faux, le bouton aussi.
          setEtat({ ...ETAT_EMAIL_INITIAL, email, erreur });
        }
      }, delai);
    },
    [annuler, delai]
  );

  useEffect(() => annuler, [annuler]);

  return { ...etat, verifier };
};

/**
 * Mutation de création : compte + demande + lien d'activation, côté serveur.
 *
 * @returns {{creer: Function, enCours: boolean, erreur: Error|null, resultat: object|undefined,
 *            reinitialiser: Function}}
 */
export const useCreateProspectAccount = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ placeId, email, form }) => {
      // Garde A7 : la callable refuse aussi (`invalid-argument` + `details.fields`), mais la modale
      // peut afficher un formulaire incomplet sans consommer un aller-retour réseau.
      const erreurs = validateStrict(form);
      if (Object.keys(erreurs).length > 0) {
        const erreur = new Error('Le formulaire est incomplet');
        erreur.code = 'invalid-argument';
        erreur.details = { fields: erreurs };
        throw erreur;
      }
      const resultat = await createProspectAccount({ placeId, email, form });
      return resultat;
    },
    onSuccess: () => {
      // La fiche passe en `demande_creee` : sans invalidation, la file d'appel la garde jusqu'au
      // rechargement (même raison que dans `useProspects`).
      queryClient.invalidateQueries({ queryKey: [PROSPECTS_KEY] });
    }
  });

  return {
    creer: mutation.mutateAsync,
    enCours: mutation.isPending,
    erreur: mutation.error,
    resultat: mutation.data,
    reinitialiser: mutation.reset
  };
};

/**
 * Message affichable pour une erreur de la callable (§2.4, état `error`).
 *
 * Le `message` du serveur passe en priorité quand il existe : il est déjà rédigé pour l'agent
 * (« Un compte MonSiteDirect utilise déjà cette adresse… »), et le remplacer par un texte générique
 * ferait perdre l'information actionnable.
 *
 * @param {Error|null} erreur erreur remontée par `useCreateProspectAccount`
 * @returns {string|null}
 */
export const messageErreurDemande = (erreur) => {
  if (!erreur) return null;

  const repli = {
    'functions/unauthenticated': 'Session expirée : reconnectez-vous puis réessayez.',
    'functions/permission-denied': "Votre compte n'a pas les droits pour créer une demande.",
    'functions/not-found': "Cette fiche prospect n'existe plus.",
    'functions/failed-precondition': 'Le statut de la fiche ne permet pas de créer une demande.',
    'functions/already-exists': 'Un compte existe déjà avec cette adresse.',
    'functions/invalid-argument': 'Le formulaire est incomplet.',
    'functions/internal': "Erreur serveur : aucune donnée n'a été conservée, réessayez."
  };

  return erreur.message || repli[erreur.code] || 'Création impossible, réessayez.';
};
